import {
  ActionIcon, Alert, Anchor, Badge, Button, Card, Group, Menu, Modal, NumberInput, SegmentedControl, Select, SimpleGrid, Stack, Text,
  TextInput, Textarea, Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconCalendarPlus, IconCheck, IconDots, IconNotebook, IconPlayerSkipForward, IconPlus, IconRotate, IconTrash,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";

import { http } from "../../api/http";
import { useCan } from "../../auth/user";
import { Empty, fmtDate } from "../../components/ui";
import { useT } from "../../i18n/script";
import { productLine, type Procedure, TYPE_COLOR, TYPE_SHORT } from "../procedures/common";
import { EntryModal, type PlanPreset } from "./DiaryTab";
import type { Orchard } from "./OrchardDetailPage";

interface Task extends PlanPreset {
  season: number; procedure_id?: number | null; procedure_name?: string | null; phase?: string | null; bbch_to?: number | null;
  planned_from: string; planned_to: string; mandatory: boolean; status: "planirano" | "uradjeno" | "preskoceno"; entry_id?: number | null;
  entry_date?: string | null; note?: string | null; late: boolean; due: boolean;
}
interface Plan {
  season: number; tasks: Task[]; procedures: { id: number; name: string; part_of_orchard?: string | null }[];
  summary: { planirano: number; uradjeno: number; preskoceno: number; kasni: number };
}
const MONTHS = ["Januar", "Februar", "Mart", "April", "Maj", "Jun", "Jul", "Avgust", "Septembar", "Oktobar", "Novembar", "Decembar"];

function ApplyHere({ orchard, season, onClose }: { orchard: Orchard; season: number; onClose: () => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const species = orchard.species_all?.length ? orchard.species_all : [orchard.species];
  const procs = useQuery({ queryKey: ["procedures", null, false], queryFn: () => http.get<Procedure[]>("/api/v1/procedures") });
  const list = (procs.data ?? []).filter((p) => species.includes(p.species_code));
  const [pid, setPid] = useState<string | null>(null);
  const [part, setPart] = useState("");
  const [shift, setShift] = useState<number | string>(0);
  const go = useMutation({
    mutationFn: () => http.post<{ created: number }>(`/api/v1/orchards/${orchard.id}/plan`, {
      procedure_id: Number(pid), season, part_of_orchard: part || null, shift_days: Number(shift) || 0 }),
    onSuccess: (r) => { notifications.show({ color: "green", message: `${t("Dodato stavki plana")}: ${r.created}` }); qc.invalidateQueries({ queryKey: ["plan"] }); onClose(); },
    onError: (e: Error) => notifications.show({ color: "red", message: e.message }),
  });
  return (
    <Modal opened onClose={onClose} title={`${t("Plan radova iz tehnološkog postupka")} — ${season}`} size="lg">
      <Stack>
        <Select label={t("Postupak")} required value={pid} onChange={setPid} searchable
          data={list.map((p) => ({ value: String(p.id), label: `${p.name}${p.owner ? ` (${t("lični")})` : ""}` }))}
          description={species.length > 1 ? t("Miješani zasad: primijenite po jedan postupak za svaku vrstu i upišite dio voćnjaka (redove).") : undefined} />
        <SimpleGrid cols={2}>
          <TextInput label={t("Dio voćnjaka")} placeholder={species.length > 1 ? t("npr. redovi 4–5 (kruška)") : t("cijeli voćnjak")}
            value={part} onChange={(e) => setPart(e.currentTarget.value)} />
          <NumberInput label={t("Pomjeranje rokova (dana)")} min={-60} max={60} value={shift} onChange={setShift} />
        </SimpleGrid>
        <Anchor component={Link} to="/postupci" size="sm">{t("Pregled i izmjena postupaka")}</Anchor>
        <Group justify="flex-end"><Button disabled={!pid} loading={go.isPending} onClick={() => go.mutate()}>{t("Napravi plan")}</Button></Group>
      </Stack>
    </Modal>
  );
}

function AddTask({ orchard, season, onClose }: { orchard: Orchard; season: number; onClose: () => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const types = useQuery({ queryKey: ["entry-types"], queryFn: () => http.get<{ code: string; name: string }[]>("/api/v1/diary/entry-types") });
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ entry_type: "ostalo", title: "", planned_from: today, planned_to: today, description: "", part_of_orchard: "" });
  const go = useMutation({
    mutationFn: () => http.post(`/api/v1/orchards/${orchard.id}/plan/tasks`, { ...f, season, part_of_orchard: f.part_of_orchard || null }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["plan"] }); onClose(); },
    onError: (e: Error) => notifications.show({ color: "red", message: e.message }),
  });
  return (
    <Modal opened onClose={onClose} title={t("Nova stavka plana")}>
      <Stack>
        <Select label={t("Radna operacija")} data={(types.data ?? []).map((x) => ({ value: x.code, label: x.name }))} value={f.entry_type}
          onChange={(v) => setF({ ...f, entry_type: v ?? "ostalo" })} allowDeselect={false} />
        <TextInput label={t("Naziv")} required value={f.title} onChange={(e) => setF({ ...f, title: e.currentTarget.value })} />
        <SimpleGrid cols={2}>
          <TextInput type="date" label={t("Od")} value={f.planned_from} onChange={(e) => setF({ ...f, planned_from: e.currentTarget.value })} />
          <TextInput type="date" label={t("Do")} value={f.planned_to} onChange={(e) => setF({ ...f, planned_to: e.currentTarget.value })} />
        </SimpleGrid>
        <TextInput label={t("Dio voćnjaka")} value={f.part_of_orchard} onChange={(e) => setF({ ...f, part_of_orchard: e.currentTarget.value })} />
        <Textarea label={t("Opis")} value={f.description} onChange={(e) => setF({ ...f, description: e.currentTarget.value })} />
        <Group justify="flex-end"><Button disabled={f.title.trim().length < 2} loading={go.isPending} onClick={() => go.mutate()}>{t("Dodaj")}</Button></Group>
      </Stack>
    </Modal>
  );
}

export function PlanTab({ orchard, season }: { orchard: Orchard; season: number }) {
  const { t } = useT();
  const can = useCan();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["plan", orchard.id, season], queryFn: () => http.get<Plan>(`/api/v1/orchards/${orchard.id}/plan`, { season }) });
  const [filter, setFilter] = useState("sve");
  const [apply, setApply] = useState(false);
  const [add, setAdd] = useState(false);
  const [entry, setEntry] = useState<Task | null>(null);
  const patch = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) => http.patch(`/api/v1/plan-tasks/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["plan"] }),
  });
  const del = useMutation({
    mutationFn: (id: number) => http.del(`/api/v1/plan-tasks/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["plan"] }),
  });
  const w = can("diary:write");
  const tasks = (q.data?.tasks ?? []).filter((x) => filter === "sve" || (filter === "otvoreno" ? x.status === "planirano"
    : filter === "kasni" ? x.late : filter === "sada" ? x.due : x.status !== "planirano"));
  const byMonth = new Map<number, Task[]>();
  for (const x of tasks) {
    const m = new Date(x.planned_from).getMonth();
    byMonth.set(m, [...(byMonth.get(m) ?? []), x]);
  }
  const s = q.data?.summary;
  return (
    <Stack>
      <Group justify="space-between">
        <Group gap="xs">
          {s && <>
            <Badge size="lg" variant="light">{t("planirano")}: {s.planirano}</Badge>
            <Badge size="lg" variant="light" color="green">{t("urađeno")}: {s.uradjeno}</Badge>
            <Badge size="lg" variant="light" color="gray">{t("preskočeno")}: {s.preskoceno}</Badge>
            {s.kasni > 0 && <Badge size="lg" color="red">{t("kasni")}: {s.kasni}</Badge>}
          </>}
        </Group>
        {w && (
          <Group gap="xs">
            <Button variant="default" leftSection={<IconPlus size={16} />} onClick={() => setAdd(true)}>{t("Stavka")}</Button>
            <Button leftSection={<IconCalendarPlus size={16} />} onClick={() => setApply(true)}>{t("Plan iz postupka")}</Button>
          </Group>
        )}
      </Group>
      {q.data?.procedures?.length ? (
        <Text size="sm" c="dimmed">
          {t("Postupci")}: {q.data.procedures.map((p, i) => (
            <span key={`${p.id}-${i}`}>{i > 0 && " · "}<Anchor component={Link} to={`/postupci?id=${p.id}`} size="sm">{p.name}</Anchor>{p.part_of_orchard ? ` (${p.part_of_orchard})` : ""}</span>
          ))}
        </Text>
      ) : null}
      <SegmentedControl w="fit-content" value={filter} onChange={setFilter} data={[
        { value: "sve", label: t("Sve") }, { value: "sada", label: t("Sada na redu") }, { value: "kasni", label: t("Kasni") },
        { value: "otvoreno", label: t("Otvoreno") }, { value: "zavrseno", label: t("Završeno") }]} />
      {!q.data?.tasks.length && !q.isLoading && (
        <Alert color="blue" variant="light">
          {t("Za ovu sezonu nema plana radova. „Plan iz postupka“ pravi plan iz tehnološkog postupka za vrstu i sortu (zaštita, ishrana, navodnjavanje, rezidba, berba) — svaku stavku potom upisujete u poljoprivrednu knjigu jednim klikom.")}
        </Alert>
      )}
      {!tasks.length && !!q.data?.tasks.length && <Empty text="Nema stavki za izabrani filter" />}
      {[...byMonth.entries()].sort((a, b) => a[0] - b[0]).map(([m, list]) => (
        <div key={m}>
          <Text fw={600} size="sm" mb={6}>{t(MONTHS[m])}</Text>
          <Stack gap={6}>
            {list.map((x) => (
              <Card key={x.id} withBorder radius="md" p="sm" style={{
                borderLeft: `4px solid var(--mantine-color-${TYPE_COLOR[x.entry_type] ?? "gray"}-6)`,
                opacity: x.status === "preskoceno" ? 0.55 : 1,
              }}>
                <Group justify="space-between" wrap="nowrap" align="flex-start">
                  <div style={{ minWidth: 0 }}>
                    <Group gap={6}>
                      <Badge size="xs" color={TYPE_COLOR[x.entry_type] ?? "gray"} variant={x.mandatory ? "filled" : "outline"}>{t(TYPE_SHORT[x.entry_type] ?? x.entry_type)}</Badge>
                      <Text fw={500} size="sm">{x.title}</Text>
                      {x.status === "uradjeno" && <Badge size="xs" color="green" leftSection={<IconCheck size={10} />}>{t("urađeno")}{x.entry_date ? ` ${fmtDate(x.entry_date)}` : ""}</Badge>}
                      {x.status === "preskoceno" && <Badge size="xs" color="gray">{t("preskočeno")}</Badge>}
                      {x.late && <Badge size="xs" color="red">{t("kasni")}</Badge>}
                      {x.due && <Badge size="xs" color="orange">{t("sada")}</Badge>}
                      {!x.mandatory && <Badge size="xs" variant="outline" color="gray">{t("po potrebi")}</Badge>}
                    </Group>
                    <Text size="xs" c="dimmed">{fmtDate(x.planned_from)} – {fmtDate(x.planned_to)}{x.bbch_from != null ? ` · BBCH ${x.bbch_from}${x.bbch_to !== x.bbch_from ? `–${x.bbch_to}` : ""}` : ""}{x.part_of_orchard ? ` · ${x.part_of_orchard}` : ""}</Text>
                    {x.products.length > 0 && <Text size="xs" data-no-translit="true">{x.products.map(productLine).join(" + ")}</Text>}
                    {x.condition && <Text size="xs" c="orange.8">{t("Uslov")}: {x.condition}</Text>}
                    {x.note && <Text size="xs" c="dimmed">{t("Napomena")}: {x.note}</Text>}
                  </div>
                  {w && (
                    <Group gap={4} wrap="nowrap">
                      {x.status === "planirano" && (
                        <Tooltip label={t("Otvori upis u poljoprivrednu knjigu sa podacima iz plana")}>
                          <Button size="xs" variant="light" leftSection={<IconNotebook size={14} />} onClick={() => setEntry(x)}>{t("Upiši u knjigu")}</Button>
                        </Tooltip>
                      )}
                      <Menu position="bottom-end" withinPortal>
                        <Menu.Target><ActionIcon variant="subtle"><IconDots size={16} /></ActionIcon></Menu.Target>
                        <Menu.Dropdown>
                          {x.status === "planirano"
                            ? <>
                                <Menu.Item leftSection={<IconCheck size={14} />} onClick={() => patch.mutate({ id: x.id, body: { status: "uradjeno" } })}>{t("Označi urađeno (bez upisa)")}</Menu.Item>
                                <Menu.Item leftSection={<IconPlayerSkipForward size={14} />} onClick={() => patch.mutate({ id: x.id, body: { status: "preskoceno", note: x.condition ? t("uslov nije ispunjen") : undefined } })}>{t("Preskoči (nije potrebno)")}</Menu.Item>
                              </>
                            : <Menu.Item leftSection={<IconRotate size={14} />} onClick={() => patch.mutate({ id: x.id, body: { status: "planirano" } })}>{t("Vrati u planirano")}</Menu.Item>}
                          <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={() => del.mutate(x.id)}>{t("Ukloni iz plana")}</Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Group>
                  )}
                </Group>
              </Card>
            ))}
          </Stack>
        </div>
      ))}
      {apply && <ApplyHere orchard={orchard} season={season} onClose={() => setApply(false)} />}
      {add && <AddTask orchard={orchard} season={season} onClose={() => setAdd(false)} />}
      {entry && <EntryModal orchard={orchard} preset={entry} onClose={() => setEntry(null)} />}
    </Stack>
  );
}
